import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowLeft, Plus, Trash2, ExternalLink, Mail, Phone, Building2, Send, MessageCircle } from 'lucide-react';
import {
  pipelineService,
  ETAPAS,
  CANAIS,
  INTERACAO_TIPOS,
  canalMeta,
  etapaMeta,
  formatCurrency,
  interpolate,
  tipoAssetMeta,
  type PipelineAsset,
  type PipelineCanal,
  type InteracaoTipo,
} from '@/features/pipeline/services/pipeline.service';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';

export const Route = createFileRoute('/_authenticated/pipeline/leads/$id')({
  component: LeadDetail,
});

function LeadDetail() {
  return (
    <PermissionGate permission={Permission.PIPELINE_VIEW}>
      <LeadDetailContent />
    </PermissionGate>
  );
}

function LeadDetailContent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();

  const leadQ = useQuery({ queryKey: ['pipeline-lead', id], queryFn: () => pipelineService.getLead(id) });
  const interQ = useQuery({ queryKey: ['pipeline-interacoes', id], queryFn: () => pipelineService.listInteracoes(id) });

  const updateM = useMutation({
    mutationFn: (dto: any) => pipelineService.updateLead(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipeline-lead', id] });
      qc.invalidateQueries({ queryKey: ['pipeline-leads'] });
    },
  });

  const delM = useMutation({
    mutationFn: () => pipelineService.deleteLead(id),
    onSuccess: () => {
      toast.success('Lead removido');
      qc.invalidateQueries({ queryKey: ['pipeline-leads'] });
      navigate({ to: '/pipeline/leads' });
    },
  });

  if (leadQ.isLoading) return <div className="p-6 text-muted-foreground">Carregando...</div>;
  if (!leadQ.data) return <div className="p-6 text-muted-foreground">Lead não encontrado.</div>;
  const lead = leadQ.data;
  const cm = canalMeta(lead.canalOrigem);
  const em = etapaMeta(lead.etapa);
  const Cm = cm.icon;
  const interacoes = interQ.data ?? [];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <Link
        to="/pipeline/leads"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Leads
      </Link>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <h1 className="font-display text-3xl font-semibold tracking-tight">{lead.nome}</h1>
                <Badge style={{ background: em.color, color: 'white' }}>{em.label}</Badge>
              </div>
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                {lead.empresa && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5" />
                    {lead.empresa}
                  </span>
                )}
                {lead.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    {lead.email}
                  </span>
                )}
                {lead.telefone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" />
                    {lead.telefone}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Cm className="h-3.5 w-3.5" />
                  Origem: {cm.label}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={lead.etapa} onValueChange={(v) => updateM.mutate({ etapa: v as any })}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ETAPAS.map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remover lead"
                onClick={async () => {
                  if (
                    await confirm({
                      title: 'Remover este lead?',
                      description: 'Esta ação não pode ser desfeita.',
                      confirmLabel: 'Remover lead',
                      destructive: true,
                    })
                  )
                    delM.mutate();
                }}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Linha do tempo</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {interacoes.length} interaç{interacoes.length === 1 ? 'ão' : 'ões'} registrada
                  {interacoes.length === 1 ? '' : 's'}
                </p>
              </div>
              <NovaInteracaoDialog leadId={id} leadNome={lead.nome} leadEmpresa={lead.empresa} />
            </CardHeader>
            <CardContent>
              {interacoes.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-8 text-center">
                  <p className="text-sm text-muted-foreground mb-3">Nenhuma interação ainda.</p>
                  <NovaInteracaoDialog leadId={id} leadNome={lead.nome} leadEmpresa={lead.empresa} />
                </div>
              ) : (
                <ol className="relative space-y-4 pl-6 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-px before:bg-border">
                  {interacoes.map((i) => {
                    const icm = canalMeta(i.canal);
                    const Ic = icm.icon;
                    return (
                      <li key={i.id} className="relative">
                        <span className="absolute -left-6 top-1.5 grid h-6 w-6 place-items-center rounded-full bg-primary/15 ring-4 ring-card">
                          <Ic className="h-3 w-3 text-primary" />
                        </span>
                        <div className="rounded-md border border-border bg-card/50 p-3">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {INTERACAO_TIPOS.find((t) => t.value === i.tipo)?.label}
                            </span>
                            <Badge variant="outline" className="h-4 px-1.5 text-[10px]">
                              {icm.label}
                            </Badge>
                            <span>· {format(new Date(i.createdAt), "dd MMM yyyy 'às' HH:mm", { locale: ptBR })}</span>
                          </div>
                          {i.mensagem && <p className="text-sm mt-2 whitespace-pre-wrap">{i.mensagem}</p>}
                          {i.assetId && (
                            <Link to="/pipeline/assets/$id" params={{ id: i.assetId }}>
                              <Badge variant="outline" className="mt-2 text-[10px] gap-1">
                                <ExternalLink className="h-2.5 w-2.5" /> asset usado
                              </Badge>
                            </Link>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </CardContent>
          </Card>

          {lead.mensagemAbordagem && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Mensagem de abordagem inicial</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap text-muted-foreground">{lead.mensagemAbordagem}</p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Valores</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Valor da proposta</Label>
                <Input
                  type="number"
                  step="0.01"
                  defaultValue={lead.valorProposta ?? ''}
                  onBlur={(e) => {
                    const v = e.target.value === '' ? null : Number(e.target.value);
                    if (String(v) !== String(lead.valorProposta ?? null)) updateM.mutate({ valorProposta: v });
                  }}
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Valor fechado</Label>
                <Input
                  type="number"
                  step="0.01"
                  defaultValue={lead.valorFechado ?? ''}
                  onBlur={(e) => {
                    const v = e.target.value === '' ? null : Number(e.target.value);
                    if (String(v) !== String(lead.valorFechado ?? null)) updateM.mutate({ valorFechado: v });
                  }}
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Custo de aquisição</Label>
                <Input
                  type="number"
                  step="0.01"
                  defaultValue={lead.custoAquisicao ?? ''}
                  onBlur={(e) => {
                    const v = e.target.value === '' ? null : Number(e.target.value);
                    if (String(v) !== String(lead.custoAquisicao ?? null)) updateM.mutate({ custoAquisicao: v });
                  }}
                  placeholder="0,00"
                />
              </div>
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total:</span>
                <span className="font-semibold text-primary">
                  {formatCurrency(Number(lead.valorFechado ?? lead.valorProposta ?? 0))}
                </span>
              </div>
            </CardContent>
          </Card>

          {(lead.telefone || lead.contatoOmnichannelAddress) && <ConversaCard leadId={id} />}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Observações</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={5}
                defaultValue={lead.observacoes ?? ''}
                onBlur={(e) => {
                  const v = e.target.value || null;
                  if (v !== lead.observacoes) updateM.mutate({ observacoes: v });
                }}
                placeholder="Notas internas sobre este lead..."
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

const CONVERSA_LABEL: Record<'whatsapp' | 'messenger', string> = {
  whatsapp: 'WhatsApp',
  messenger: 'Messenger',
};

function ConversaCard({ leadId }: { leadId: string }) {
  const qc = useQueryClient();
  const [mensagem, setMensagem] = useState('');

  const historicoQ = useQuery({
    queryKey: ['pipeline-lead-conversa', leadId],
    queryFn: () => pipelineService.getConversaOmnichannel(leadId),
  });

  const sendM = useMutation({
    mutationFn: () => pipelineService.sendConversaOmnichannel(leadId, mensagem),
    onSuccess: () => {
      setMensagem('');
      qc.invalidateQueries({ queryKey: ['pipeline-lead-conversa', leadId] });
      qc.invalidateQueries({ queryKey: ['pipeline-interacoes', leadId] });
    },
    // Sem onError próprio: o interceptor global do axios (lib/axios.ts) já mostra
    // um toast com a mensagem de erro real do backend para qualquer request.
  });

  const mensagens = historicoQ.data?.mensagens ?? [];
  const canal = historicoQ.data?.conversa?.channel;
  const titulo = canal ? CONVERSA_LABEL[canal] : 'Conversa';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-primary" /> {titulo}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!historicoQ.data?.conversa ? (
          <p className="text-xs text-muted-foreground">Nenhuma conversa encontrada para este lead.</p>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-2">
            {mensagens.map((m) => (
              <div
                key={m.id}
                className={`rounded-md p-2 text-sm max-w-[85%] ${
                  m.direction === 'outbound' ? 'ml-auto bg-primary/15' : 'bg-secondary'
                }`}
              >
                <p className="whitespace-pre-wrap">{m.body}</p>
                <span className="text-[10px] text-muted-foreground">
                  {format(new Date(m.createdAt), 'dd MMM HH:mm', { locale: ptBR })}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Textarea
            rows={2}
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            placeholder={`Responder pelo ${titulo}...`}
          />
          <Button size="icon" aria-label="Enviar mensagem" onClick={() => sendM.mutate()} disabled={!mensagem || sendM.isPending}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function NovaInteracaoDialog({
  leadId,
  leadNome,
  leadEmpresa,
}: {
  leadId: string;
  leadNome: string;
  leadEmpresa: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-1" /> Registrar interação
        </Button>
      </DialogTrigger>
      <DialogContent size="lg" busy={busy}>
        <DialogHeader>
          <DialogTitle>Nova interação</DialogTitle>
        </DialogHeader>
        {/* estado dentro do DialogContent: reabre limpo */}
        <NovaInteracaoForm
          leadId={leadId}
          leadNome={leadNome}
          leadEmpresa={leadEmpresa}
          onBusyChange={setBusy}
          onClose={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

export function NovaInteracaoForm({
  leadId,
  leadNome,
  leadEmpresa,
  onClose,
  onBusyChange,
}: {
  leadId: string;
  leadNome: string;
  leadEmpresa: string | null;
  onClose: () => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [canal, setCanal] = useState<PipelineCanal>('whatsapp');
  const [tipo, setTipo] = useState<InteracaoTipo>('followup');
  const [mensagem, setMensagem] = useState('');
  const [assetId, setAssetId] = useState<string | null>(null);
  // texto digitado antes de aplicar o asset, para "Remover asset" restaurar
  const [applied, setApplied] = useState<{ text: string; prev: string } | null>(null);

  const assetsQ = useQuery({ queryKey: ['pipeline-assets'], queryFn: pipelineService.listAssets });
  const assetsFiltrados = useMemo<PipelineAsset[]>(() => {
    if (!assetsQ.data) return [];
    return assetsQ.data.filter((a) => a.canaisSugeridos.length === 0 || a.canaisSugeridos.includes(canal));
  }, [assetsQ.data, canal]);

  const m = useMutation({
    mutationFn: () =>
      pipelineService.createInteracao(leadId, {
        canal,
        tipo,
        mensagem: mensagem || undefined,
        assetId: assetId ?? undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipeline-interacoes', leadId] });
      qc.invalidateQueries({ queryKey: ['pipeline-dashboard'] });
      qc.invalidateQueries({ queryKey: ['pipeline-assets'] });
      toast.success('Interação registrada');
      onClose();
    },
  });

  useEffect(() => onBusyChange?.(m.isPending), [m.isPending, onBusyChange]);

  async function applyAsset(a: PipelineAsset) {
    if (a.conteudoTexto) {
      const text = interpolate(a.conteudoTexto, { nome: leadNome, empresa: leadEmpresa });
      // só pergunta se há texto digitado à mão (não o que um asset anterior preencheu)
      if (mensagem.trim() && mensagem !== applied?.text) {
        const ok = await confirm({
          title: 'Substituir a mensagem?',
          description: 'O texto que você digitou será trocado pelo conteúdo do asset.',
          confirmLabel: 'Substituir mensagem',
        });
        if (!ok) return;
      }
      setApplied({ text, prev: mensagem === applied?.text ? applied.prev : mensagem });
      setMensagem(text);
    }
    setAssetId(a.id);
  }

  function removeAsset() {
    setAssetId(null);
    if (applied && mensagem === applied.text) setMensagem(applied.prev);
    setApplied(null);
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Canal</Label>
          <Select value={canal} onValueChange={(v) => setCanal(v as PipelineCanal)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CANAIS.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Tipo</Label>
          <Select value={tipo} onValueChange={(v) => setTipo(v as InteracaoTipo)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTERACAO_TIPOS.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Usar asset</Label>
        <div className="max-h-40 overflow-y-auto rounded-md border border-border p-1 space-y-1">
          {assetsFiltrados.length === 0 && (
            <p className="text-xs text-muted-foreground p-2">
              Nenhum asset compatível.{' '}
              <Link to="/pipeline/assets/novo" className="text-primary hover:underline">
                Criar
              </Link>
            </p>
          )}
          {assetsFiltrados.map((a) => {
            const tm = tipoAssetMeta(a.tipo);
            const Tc = tm.icon;
            const sel = assetId === a.id;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => applyAsset(a)}
                className={`w-full text-left flex items-center gap-2 rounded-md p-2 text-sm transition ${
                  sel ? 'bg-primary/20 ring-1 ring-primary' : 'hover:bg-secondary'
                }`}
              >
                <Tc className="h-4 w-4 text-primary shrink-0" />
                <span className="flex-1 truncate">{a.titulo}</span>
                <span className="text-[10px] text-muted-foreground">{tm.label}</span>
              </button>
            );
          })}
        </div>
        {assetId && (
          <Button type="button" variant="ghost" size="sm" onClick={removeAsset} className="text-xs h-6">
            Remover asset
          </Button>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="interacao-mensagem" className="text-xs">
          Mensagem
        </Label>
        <Textarea
          id="interacao-mensagem"
          rows={6}
          value={mensagem}
          onChange={(e) => setMensagem(e.target.value)}
          placeholder="O que foi enviado / discutido..."
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={m.isPending}>
          Cancelar
        </Button>
        <Button type="submit" loading={m.isPending}>
          Registrar interação
        </Button>
      </DialogFooter>
    </form>
  );
}
